import { Button, Stack, Typography } from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useToastNotification } from "../providers";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

// HOC to inject toast notification hook into class component
const withToastNotification = (WrappedComponent: any) => {
  return (props: any) => {
    const toastNotification = useToastNotification();
    return (
      <WrappedComponent {...props} toastNotification={toastNotification} />
    );
  };
};

class ErrorBoundaryComponent extends Component<
  Props & { toastNotification: any },
  State
> {
  constructor(props: Props & { toastNotification: any }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Global Error Boundary caught an error:", error, errorInfo);

    const errorMessage = error.message || "An unexpected error occurred";
    this.props.toastNotification.showError(`Error: ${errorMessage}`);
  }

  render() {
    if (this.state.hasError) {
      // Reset error state after showing notification
      setTimeout(() => {
        this.setState({ hasError: false });
      }, 100);

      // Return children to allow app to continue working
      return this.props.children;
    }

    return this.props.children;
  }
}

export const _GlobalErrorBoundary = withToastNotification(
  ErrorBoundaryComponent
);

export const GlobalErrorBoundary: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  return (
    <ErrorBoundary
      FallbackComponent={() => (
        <Stack
          spacing={2}
          alignItems="center"
          justifyContent="center"
          sx={{ height: "100%" }}
        >
          <Typography variant="h1">Ops! Qualcosa è andato storto. </Typography>
          <Button onClick={() => window.history.back()}>Indietro</Button>
        </Stack>
      )}
      onError={() => {}}
    >
      {children}
    </ErrorBoundary>
  );
};
